/**
 * Pie Chart Component
 */

import { IBuilderComponent } from '../../base'
import type {
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  formatComponentAlignmentFormat,
  formatFromVariableText,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseSpacingFormat,
  parseTypographyFormat,
  parseToVariableText,
  parseWidthFormat,
} from '../../parser-utils'
import pieChartTemplate from './pie-chart.template.json'

const PIE_CHART_DEFAULT_TEMPLATE = pieChartTemplate as unknown as IPieChartComponentSettings

export interface IPieChartComponentSettings {
  // Data settings
  'pie-chart-data-source': string
  'pie-chart-data-label-column': string
  'pie-chart-data-value-column': string
  'pie-chart-data-heading': VariableTextValue
  'pie-chart-data-sort-by'?: string
  'pie-chart-data-sort-type'?: string
  'pie-chart-data-filter'?: any
  'pie-chart-data-items-per-page'?: number[]

  // Design settings
  'pie-chart-design-color-sequence'?: string
  'pie-chart-design-legend'?: boolean
  'pie-chart-design-label'?: boolean
  'pie-chart-design-doughnut'?: boolean
  'pie-chart-design-abbr-number'?: boolean
  'pie-chart-design-width'?: ResponsiveValue<string[] | number[]>
  'pie-chart-design-alignment'?: ResponsiveValue<string>
  'pie-chart-design-heading-alignment'?: ResponsiveValue<string>
  'pie-chart-design-component-margin'?: ResponsiveValue<SpacingInputValue>
  'pie-chart-design-heading-margin'?: ResponsiveValue<SpacingInputValue>
  'pie-chart-design-typography'?: ResponsiveValue<TypographyValue>
  'pie-chart-design-heading-typography'?: TypographyValue
}

export interface IPieChartComponent extends IBuilderComponent {
  componentId: 'pie'
  groupId: 'chart'
  settings: IPieChartComponentSettings
}

export interface AiPieChartInput {
  dataSource: string
  labelColumn?: string
  valueColumn?: string
  heading?: string[] // Format: ["text:Sales Chart"] or ["text:Revenue by ", "cr:category"]
  sortBy?: string
  sortType?: string
  filter?: any
  itemsPerPage?: number
  width?: string
  alignment?: string
  headingAlignment?: string
  margin?: string
  headingMargin?: string
  typography?: string
  headingTypography?: string
  colorSequence?: string
  legend?: boolean
  label?: boolean
  doughnut?: boolean
  abbrNumber?: boolean
}

export class PieChartComp {
  getTemplate(): IPieChartComponentSettings {
    return PIE_CHART_DEFAULT_TEMPLATE
  }

  /**
   * format pie chart component settings to AI pie chart input
   */
  format(settings: IPieChartComponentSettings): AiPieChartInput {
    const template = this.getTemplate()
    const result: AiPieChartInput = {
      dataSource: settings['pie-chart-data-source'],
    }

    // Label Column
    if (settings['pie-chart-data-label-column']) {
      result.labelColumn = settings['pie-chart-data-label-column']
    }

    // Value Column
    if (settings['pie-chart-data-value-column']) {
      result.valueColumn = settings['pie-chart-data-value-column']
    }

    // Heading
    if (settings['pie-chart-data-heading']) {
      const heading = formatFromVariableText(settings['pie-chart-data-heading'])
      if (heading.length > 0 && heading[0] !== 'text:') {
        result.heading = heading
      }
    }

    // Sort By
    if (settings['pie-chart-data-sort-by']) {
      result.sortBy = settings['pie-chart-data-sort-by']
    }

    // Sort Type
    if (settings['pie-chart-data-sort-type']) {
      result.sortType = settings['pie-chart-data-sort-type']
    }

    // Filter
    if (settings['pie-chart-data-filter']) {
      result.filter = settings['pie-chart-data-filter']
    }

    // Items Per Page
    if (settings['pie-chart-data-items-per-page'] && settings['pie-chart-data-items-per-page'].length > 0) {
      result.itemsPerPage = settings['pie-chart-data-items-per-page'][0]
    }

    // Width
    const width = formatWidthFormat(settings['pie-chart-design-width'])
    if (width) {
      result.width = width
    }

    // Alignment
    if (settings['pie-chart-design-alignment']) {
      const alignment = formatComponentAlignmentFormat(settings['pie-chart-design-alignment'] as any)
      if (alignment) {
        result.alignment = alignment
      }
    }

    // Heading Alignment
    if (settings['pie-chart-design-heading-alignment']) {
      const headingAlignment = formatComponentAlignmentFormat(settings['pie-chart-design-heading-alignment'] as any)
      if (headingAlignment) {
        result.headingAlignment = headingAlignment
      }
    }

    // Margin
    const margin = formatSpacingFormat(settings['pie-chart-design-component-margin'])
    if (margin) {
      result.margin = margin
    }

    // Heading Margin
    const headingMargin = formatSpacingFormat(settings['pie-chart-design-heading-margin'])
    if (headingMargin) {
      result.headingMargin = headingMargin
    }

    // Typography
    const typography = formatTypographyFormat(settings['pie-chart-design-typography'])
    if (typography) {
      result.typography = typography
    }

    // Heading Typography
    if (settings['pie-chart-design-heading-typography']) {
      const headingTypo = formatTypographyFormat({
        phone: settings['pie-chart-design-heading-typography'],
        'phone-landscape': settings['pie-chart-design-heading-typography'],
        tablet: settings['pie-chart-design-heading-typography'],
        'tablet-landscape': settings['pie-chart-design-heading-typography'],
        desktop: settings['pie-chart-design-heading-typography'],
        tv: settings['pie-chart-design-heading-typography'],
      } as any)
      if (headingTypo) {
        result.headingTypography = headingTypo
      }
    }

    // Color Sequence
    if (settings['pie-chart-design-color-sequence'] && settings['pie-chart-design-color-sequence'] !== template['pie-chart-design-color-sequence']) {
      result.colorSequence = settings['pie-chart-design-color-sequence']
    }

    // Legend
    if (settings['pie-chart-design-legend'] !== undefined && settings['pie-chart-design-legend'] !== template['pie-chart-design-legend']) {
      result.legend = settings['pie-chart-design-legend']
    }

    // Label
    if (settings['pie-chart-design-label'] !== undefined && settings['pie-chart-design-label'] !== template['pie-chart-design-label']) {
      result.label = settings['pie-chart-design-label']
    }

    // Doughnut
    if (settings['pie-chart-design-doughnut'] !== undefined && settings['pie-chart-design-doughnut'] !== template['pie-chart-design-doughnut']) {
      result.doughnut = settings['pie-chart-design-doughnut']
    }

    // Abbr Number
    if (settings['pie-chart-design-abbr-number'] !== undefined && settings['pie-chart-design-abbr-number'] !== template['pie-chart-design-abbr-number']) {
      result.abbrNumber = settings['pie-chart-design-abbr-number']
    }

    return result
  }

  /**
   * parse AI pie chart input to pie chart component settings
   */
  parse(input: AiPieChartInput): IPieChartComponentSettings {
    const result = deepMerge(PIE_CHART_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['pie-chart-data-source'] = input.dataSource
    }

    // Data - Label Column
    if (input.labelColumn) {
      result['pie-chart-data-label-column'] = input.labelColumn
    }

    // Data - Value Column
    if (input.valueColumn) {
      result['pie-chart-data-value-column'] = input.valueColumn
    }

    // Data - Heading
    if (input.heading !== undefined) {
      result['pie-chart-data-heading'] = parseToVariableText(input.heading)
    }

    // Data - Sort By
    if (input.sortBy) {
      result['pie-chart-data-sort-by'] = input.sortBy
    }

    // Data - Sort Type
    if (input.sortType) {
      result['pie-chart-data-sort-type'] = input.sortType
    }

    // Data - Filter
    if (input.filter) {
      result['pie-chart-data-filter'] = input.filter
    }

    // Data - Items Per Page
    if (input.itemsPerPage !== undefined) {
      result['pie-chart-data-items-per-page'] = [input.itemsPerPage]
    }

    // Design - Width
    if (input.width) {
      result['pie-chart-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentMap: Record<string, string> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      const alignment = alignmentMap[input.alignment] || 'parent'
      result['pie-chart-design-alignment'] = {
        phone: alignment,
        'phone-landscape': alignment,
        tablet: alignment,
        'tablet-landscape': alignment,
        desktop: alignment,
        tv: alignment,
      } as ResponsiveValue<string>
    }

    // Design - Heading Alignment
    if (input.headingAlignment) {
      const alignmentMap: Record<string, string> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      const alignment = alignmentMap[input.headingAlignment] || 'center'
      result['pie-chart-design-heading-alignment'] = {
        phone: alignment,
        'phone-landscape': alignment,
        tablet: alignment,
        'tablet-landscape': alignment,
        desktop: alignment,
        tv: alignment,
      } as ResponsiveValue<string>
    }

    // Design - Margin
    if (input.margin) {
      result['pie-chart-design-component-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Heading Margin
    if (input.headingMargin) {
      result['pie-chart-design-heading-margin'] = parseSpacingFormat(input.headingMargin)
    }

    // Design - Typography
    if (input.typography) {
      result['pie-chart-design-typography'] = parseTypographyFormat(input.typography)
    }

    // Design - Heading Typography
    if (input.headingTypography) {
      const typo = parseTypographyFormat(input.headingTypography)
      // Apply to heading typography (single TypographyValue, not ResponsiveValue)
      const firstBreakpoint = Object.values(typo)[0]
      if (firstBreakpoint) {
        result['pie-chart-design-heading-typography'] = firstBreakpoint
      }
    }

    // Design - Color Sequence
    if (input.colorSequence) {
      result['pie-chart-design-color-sequence'] = input.colorSequence
    }

    // Design - Legend
    if (input.legend !== undefined) {
      result['pie-chart-design-legend'] = input.legend
    }

    // Design - Label
    if (input.label !== undefined) {
      result['pie-chart-design-label'] = input.label
    }

    // Design - Doughnut
    if (input.doughnut !== undefined) {
      result['pie-chart-design-doughnut'] = input.doughnut
    }

    // Design - Abbr Number
    if (input.abbrNumber !== undefined) {
      result['pie-chart-design-abbr-number'] = input.abbrNumber
    }

    return result
  }
}

